import { useCallback, useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { Clock, ImageIcon, Loader2, Smartphone } from "lucide-react";
import { StandardModal } from "@/components/StandardModal";
import { ModalPrimaryAction } from "@/components/ModalPrimaryAction";
import { MODAL_BODY_CLASS, MODAL_PANEL_MAX_HEIGHT_CLASS } from "@/lib/modalStyles";
import { api, type AiStockAnalyzedItem } from "@/lib/api";
import { parseMySQLDateLocal } from "@/lib/dateUtils";
import { toast } from "@/lib/appToast";
import { aiStockUserMessage } from "@/lib/aiStockUserMessage";
import { aiStockDevTime, optimizeAiStockImage } from "@/lib/aiStockOptimizeImage";

const ACCEPT = "image/jpeg,image/png,image/webp";

function validateImageFile(file: File): string | null {
  if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
    return "Formato inválido. Use JPG, PNG ou WEBP.";
  }
  if (file.size > 12 * 1024 * 1024) return "Imagem muito grande (máx. 12 MB).";
  if (file.size === 0) return "Arquivo vazio.";
  return null;
}

function formatCountdown(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${String(m).padStart(2, "0")}m ${String(s).padStart(2, "0")}s`;
}

type Step = 1 | 2 | 3;

type Props = {
  open: boolean;
  onClose: () => void;
  onResult: (itens: AiStockAnalyzedItem[], sessionId: number | null) => void;
};

export function ModalAiStockCapture({ open, onClose, onResult }: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sessionIdRef = useRef<number | null>(null);
  const expiresAtMsRef = useRef<number | null>(null);
  const processingRef = useRef(false);
  const resultHandledRef = useRef(false);
  const expiredHandledRef = useRef(false);
  const qrProcessingRef = useRef(false);
  const lastRetryErrorRef = useRef<string | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;

  const [sessionId, setSessionId] = useState<number | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [remainingLabel, setRemainingLabel] = useState("10m 00s");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [step, setStep] = useState<Step>(1);
  const [loading, setLoading] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(false);
  /** Loading do fluxo QR (foto recebida / IA processando no servidor). */
  const [qrProcessing, setQrProcessing] = useState(false);

  const setQrBusy = useCallback((value: boolean) => {
    qrProcessingRef.current = value;
    setQrProcessing(value);
  }, []);

  const restoreIdleUi = useCallback(() => {
    setQrBusy(false);
    setLoading(false);
    setStep(1);
    setSelectedFile(null);
    processingRef.current = false;
    resultHandledRef.current = false;
  }, [setQrBusy]);

  const stopPoll = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const stopCountdown = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const stopAllTimers = useCallback(() => {
    stopPoll();
    stopCountdown();
  }, [stopPoll, stopCountdown]);

  const cancelSession = useCallback(async () => {
    stopAllTimers();
    const id = sessionIdRef.current;
    sessionIdRef.current = null;
    expiresAtMsRef.current = null;
    if (id != null) {
      try {
        await api.aiStockCancelSession(id);
      } catch {
        /* ignore */
      }
    }
  }, [stopAllTimers]);

  const handleClose = useCallback(() => {
    void cancelSession();
    setSelectedFile(null);
    setStep(1);
    setLoading(false);
    setQrProcessing(false);
    resultHandledRef.current = false;
    processingRef.current = false;
    expiredHandledRef.current = false;
    lastRetryErrorRef.current = null;
    qrProcessingRef.current = false;
    setRemainingLabel("10m 00s");
    onClose();
  }, [cancelSession, onClose]);

  const handleExpire = useCallback(() => {
    if (expiredHandledRef.current || resultHandledRef.current || processingRef.current) return;
    expiredHandledRef.current = true;
    stopAllTimers();
    setRemainingLabel("00m 00s");
    void cancelSession().finally(() => {
      setSelectedFile(null);
      setStep(1);
      setLoading(false);
      setQrProcessing(false);
      resultHandledRef.current = false;
      processingRef.current = false;
      onCloseRef.current();
    });
  }, [cancelSession, stopAllTimers]);

  const startCountdown = useCallback(
    (expiresAtMs: number) => {
      stopCountdown();
      expiresAtMsRef.current = expiresAtMs;
      const tick = () => {
        const exp = expiresAtMsRef.current;
        if (exp == null) return;
        const left = exp - Date.now();
        if (left <= 0) {
          setRemainingLabel("00m 00s");
          handleExpire();
          return;
        }
        setRemainingLabel(formatCountdown(left));
      };
      tick();
      countdownRef.current = setInterval(tick, 250);
    },
    [handleExpire, stopCountdown],
  );

  const startPolling = useCallback(() => {
    stopPoll();
    pollRef.current = setInterval(() => {
      void (async () => {
        const sid = sessionIdRef.current;
        if (sid == null || processingRef.current || resultHandledRef.current) return;
        try {
          const st = await api.aiStockSessionStatus(sid);
          if (st.expires_at) {
            const nextExp = parseMySQLDateLocal(st.expires_at).getTime();
            if (Number.isFinite(nextExp)) {
              expiresAtMsRef.current = nextExp;
            }
          }
          if (st.status === "processing") {
            lastRetryErrorRef.current = null;
            setStep(2);
            setQrBusy(true);
            return;
          }
          if (st.status === "waiting") {
            const err = st.error?.trim() || "";
            if (qrProcessingRef.current || (err && lastRetryErrorRef.current !== err)) {
              restoreIdleUi();
            }
            if (err && lastRetryErrorRef.current !== err) {
              lastRetryErrorRef.current = err;
              toast.error(aiStockUserMessage(new Error(err)));
            }
            return;
          }
          if (st.status === "ready" && st.itens) {
            if (resultHandledRef.current || processingRef.current) return;
            resultHandledRef.current = true;
            processingRef.current = true;
            lastRetryErrorRef.current = null;
            stopAllTimers();
            setStep(2);
            setQrBusy(true);
            setStep(3);
            onResultRef.current(st.itens, sid);
            return;
          }
          if (st.status === "error") {
            stopAllTimers();
            restoreIdleUi();
            toast.error(aiStockUserMessage(new Error(st.error || "Não foi possível processar a imagem.")));
            return;
          }
          if (st.status === "expired" || st.status === "cancelled") {
            handleExpire();
          }
        } catch {
          /* polling silencioso */
        }
      })();
    }, 2500);
  }, [handleExpire, restoreIdleUi, setQrBusy, stopAllTimers, stopPoll]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    resultHandledRef.current = false;
    processingRef.current = false;
    expiredHandledRef.current = false;
    (async () => {
      setBootstrapping(true);
      setSelectedFile(null);
      setStep(1);
      setQrDataUrl("");
      setQrProcessing(false);
      setLoading(false);
      setRemainingLabel("10m 00s");
      try {
        const sess = await api.aiStockCreateSession();
        if (cancelled) {
          await api.aiStockCancelSession(sess.session_id).catch(() => undefined);
          return;
        }
        sessionIdRef.current = sess.session_id;
        setSessionId(sess.session_id);
        const expiresAtMs = parseMySQLDateLocal(sess.expires_at).getTime();
        startCountdown(expiresAtMs);

        const origin = window.location.origin;
        const base = (import.meta.env.BASE_URL || "/").replace(/\/$/, "");
        const path = `${base}/estoque/ai-captura/${encodeURIComponent(sess.token)}`.replace(
          /\/{2,}/g,
          "/",
        );
        const url = `${origin}${path.startsWith("/") ? path : `/${path}`}`;
        const dataUrl = await QRCode.toDataURL(url, { width: 200, margin: 1 });
        if (!cancelled) setQrDataUrl(dataUrl);

        startPolling();
      } catch (e) {
        if (!cancelled) {
          toast.error(e instanceof Error ? e.message : "Não foi possível iniciar a captura.");
          onClose();
        }
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    })();
    return () => {
      cancelled = true;
      stopAllTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só ao abrir
  }, [open]);

  const attachFile = (file: File | null) => {
    if (!file) return;
    const invalid = validateImageFile(file);
    if (invalid) {
      toast.error(invalid);
      return;
    }
    setSelectedFile(file);
  };

  const handleContinue = async () => {
    if (!selectedFile || processingRef.current || loading) {
      return;
    }
    processingRef.current = true;
    resultHandledRef.current = true;
    setLoading(true);
    setStep(2);
    stopAllTimers();
    try {
      const tOpt = performance.now();
      const optimized = await optimizeAiStockImage(selectedFile);
      aiStockDevTime("desktop otimização", tOpt, {
        inBytes: selectedFile.size,
        outBytes: optimized.size,
      });
      const tApi = performance.now();
      const res = await api.aiStockAnalyzeImage(optimized);
      aiStockDevTime("desktop upload→IA→matching", tApi);
      setStep(3);
      onResult(res.itens, sessionId);
      // sessão QR não usada — cancela
      if (sessionId != null) {
        await api.aiStockCancelSession(sessionId).catch(() => undefined);
        sessionIdRef.current = null;
      }
      // Mantém loading/lock até o modal fechar (evita segundo POST)
    } catch (e) {
      restoreIdleUi();
      toast.error(aiStockUserMessage(e));
      if (expiresAtMsRef.current != null) {
        startCountdown(expiresAtMsRef.current);
      }
      startPolling();
    }
  };

  const busy = loading || qrProcessing;

  return (
    <StandardModal
      open={open}
      title="Lançar estoque com imagem"
      onClose={handleClose}
      closeDisabled={busy}
      panelClassName={`!max-w-2xl ${MODAL_PANEL_MAX_HEIGHT_CLASS}`}
    >
      <div className={MODAL_BODY_CLASS}>
        {!(bootstrapping || qrProcessing || loading) ? (
          <p className="-mt-1 text-sm text-muted-foreground">
            Envie uma imagem da nota ou use o QR Code para fotografar pelo celular.
          </p>
        ) : null}

        {bootstrapping || qrProcessing || loading ? (
          <div className="flex min-h-[280px] flex-col items-center justify-center gap-2 py-12">
            <Loader2 className="h-6 w-6 animate-spin text-accent" />
            <p className="text-sm text-muted-foreground">
              {qrProcessing || loading ? "Lendo itens da imagem…" : "Preparando captura…"}
            </p>
          </div>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => fileRef.current?.click()}
                onDragEnter={(e) => {
                  e.preventDefault();
                  dragDepth.current += 1;
                  setDragging(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  dragDepth.current -= 1;
                  if (dragDepth.current <= 0) {
                    dragDepth.current = 0;
                    setDragging(false);
                  }
                }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  dragDepth.current = 0;
                  setDragging(false);
                  attachFile(e.dataTransfer.files?.[0] ?? null);
                }}
                className={`flex min-h-[220px] flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-6 text-center transition-colors ${
                  dragging ? "border-client-primary bg-client-primary/5" : "border-gray-300 bg-white"
                }`}
              >
                <ImageIcon className="h-10 w-10 text-muted-foreground" />
                <p className="text-sm font-semibold text-foreground">Anexar imagem</p>
                <p className="text-xs text-muted-foreground">PNG, JPG ou WEBP</p>
                <p className="text-xs text-muted-foreground">
                  {selectedFile
                    ? selectedFile.name
                    : "Arraste a imagem aqui ou clique para selecionar"}
                </p>
              </button>

              <div className="flex min-h-[220px] flex-col items-center justify-center gap-2 rounded-xl border border-gray-200 px-4 py-5 text-center">
                <div className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
                  <Smartphone className="h-4 w-4" />
                  Fotografar pelo celular
                </div>
                {qrDataUrl ? (
                  <img src={qrDataUrl} alt="QR Code para captura no celular" className="h-40 w-40" />
                ) : (
                  <div className="flex h-40 w-40 items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                )}
                <p className="max-w-[240px] text-xs text-muted-foreground">
                  Escaneie o QR Code para abrir a câmera no seu celular e encaminha a imagem.
                </p>
                <span
                  className="inline-flex items-center gap-1.5 rounded-full border border-black bg-white px-2.5 py-1 text-xs tabular-nums text-black"
                  aria-live="polite"
                  aria-label={`Validade do QR Code: ${remainingLabel}`}
                >
                  <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  {remainingLabel}
                </span>
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 px-3 py-3">
              <div className="grid gap-3 sm:grid-cols-3">
                {(
                  [
                    {
                      n: 1 as Step,
                      title: "Anexar ou fotografar",
                      sub: "Envie a imagem da nota ou fotografe pelo celular.",
                    },
                    {
                      n: 2 as Step,
                      title: "IA lê os itens",
                      sub: "Nossa IA identifica os itens da imagem.",
                    },
                    {
                      n: 3 as Step,
                      title: "Revisar",
                      sub: "Confira antes de registrar a entrada.",
                    },
                  ] as const
                ).map((s) => {
                  const active = step === s.n || step > s.n;
                  return (
                    <div key={s.n} className="flex gap-2">
                      <span
                        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                          active ? "bg-black text-white" : "bg-gray-200 text-gray-600"
                        }`}
                      >
                        {s.n}
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground">{s.title}</p>
                        <p className="text-xs text-muted-foreground">{s.sub}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}

        <input
          ref={fileRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => {
            attachFile(e.target.files?.[0] ?? null);
            e.target.value = "";
          }}
        />

        <ModalPrimaryAction
          show={selectedFile != null}
          type="button"
          loading={loading}
          disabled={busy || bootstrapping}
          onClick={() => void handleContinue()}
        >
          Continuar
        </ModalPrimaryAction>
      </div>
    </StandardModal>
  );
}
